import debounce from 'lodash-es/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DEBOUNCE_TIMEOUT_MS } from '@/constants';

type UseDebouncedControlledStateOptions<T> = {
  /** The controlled value from parent component */
  value: T;
  /** Callback to notify parent of changes (will be debounced) */
  onChange: (value: T) => void;
  /** Debounce delay in milliseconds (defaults to DEBOUNCE_TIMEOUT_MS) */
  debounceMs?: number;
};

/**
 * Hook that provides local state with immediate updates while debouncing
 * the controlled onChange callback. Useful for inputs that need responsive
 * UI but should throttle external calls (like API requests).
 */
export function useDebouncedControlledState<T>({
  value: controlledValue,
  onChange,
  debounceMs = DEBOUNCE_TIMEOUT_MS,
}: UseDebouncedControlledStateOptions<T>): [T, (value: T | ((prev: T) => T)) => void] {
  const [localValue, setLocalValue] = useState(controlledValue);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const debouncedOnChange = useMemo(
    () => debounce((value: T) => onChangeRef.current(value), debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    setLocalValue(controlledValue);
  }, [controlledValue]);

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const setValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      if (newValue instanceof Function) {
        setLocalValue((prev) => {
          const result = newValue(prev);
          debouncedOnChange(result);
          return result;
        });
      } else {
        setLocalValue(newValue);
        debouncedOnChange(newValue);
      }
    },
    [debouncedOnChange],
  );

  return [localValue, setValue];
}
